import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { Context } from '@/components/Context'
import { ArtData } from '@/database/art'
import Button from '@/components/Button'
import { uni1d800 } from '@/utils/extras'

const numericPower = (bp: string): number => (bp === '' || isNaN(Number(bp)) ? Number.POSITIVE_INFINITY : Number(bp))

const minimalBid = (artData: ArtData): number => {
    const highest = Number(artData.highestBid ?? 0)
    const base = artData.art.price
    const min = highest >= base ? highest * 1.05 : base
    return Number(min.toFixed(2))
}

const isDisabled = (
    artData: ArtData,
    loading: boolean,
    bidValue: string | null,
    accountSignature?: string | null,
    bidPower?: string,
): boolean => {
    const minBid = minimalBid(artData)
    const powerNum = Number(bidPower ?? 0)
    const bidNum = Number(bidValue ?? 0)

    return (
        !accountSignature ||
        !bidPower ||
        loading ||
        isNaN(bidNum) ||
        bidNum === 0 ||
        bidNum > powerNum ||
        bidNum < minBid
    )
}

const Bid = ({ artData, previousBid }: { artData: ArtData; previousBid?: number }): ReactElement => {
    const { accountSignature, tokenAmount, bidHandler, loading, setLoading } = useContext(Context)
    const [bidPower, setBidPower] = useState('')
    const [bidValue, setBidValue] = useState<string>(minimalBid(artData).toFixed(2))

    const handleBidInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(',', '.')

        // Allow intermediate numeric values (including partial decimals)
        if (/^\d*\.?\d*$/.test(raw)) {
            setBidValue(raw)
        }
    }

    useEffect(() => {
        setBidPower(tokenAmount)
    }, [tokenAmount])

    useEffect(() => {
        setLoading(false)
    }, [])

    const handleBid = () => {
        if (accountSignature) {
            const bidNum = parseFloat(bidValue)
            const minBid = minimalBid(artData)

            if (!isNaN(bidNum) && bidNum >= minBid && bidNum <= numericPower(bidPower)) {
                bidHandler(artData, bidNum, previousBid ?? 0)
                setBidValue('0')
            } else {
                setBidValue(minBid.toFixed(2))
            }
        } else {
            alert('Please connect your wallet to proceed transaction')
        }
    }

    return (
        <div className="bid-container artwork-box">
            <span className={uni1d800.className + ' gray'}>{'\uD836\uDE17CRAZY\u200C\uD836\uDE17'}</span>
            <input
                type={'range'}
                min={minimalBid(artData)}
                max={numericPower(bidPower)}
                step={'0.01'}
                value={Number(bidValue) || minimalBid(artData)}
                onChange={(event) => setBidValue(Number(event.target.value).toFixed(2))}
            />
            <input
                type="text"
                value={bidValue}
                onChange={handleBidInputChange}
                onBlur={() => {
                    const numVal = parseFloat(bidValue)
                    if (isNaN(numVal)) {
                        setBidValue(minimalBid(artData).toFixed(2))
                    } else {
                        setBidValue(Math.max(minimalBid(artData), Math.min(numVal, numericPower(bidPower))).toFixed(2))
                    }
                }}
            />
            <Button
                btnName={previousBid ? 'Increase Bid To' : 'Your bid'}
                classStyles={previousBid ? 'nav__medium long-button' : 'nav__medium'}
                disabled={isDisabled(artData, loading, bidValue, accountSignature, bidPower)}
                handleClick={handleBid}
                cancelBg={undefined}
            />
        </div>
    )
}
export default Bid
